import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { CmpsDialogRef } from '@cmps/ng-uikit';
import { Observable, of, timer } from 'rxjs';
import { filter, switchMap, takeWhile, tap } from 'rxjs/operators';

import { AuthState, AuthStatus } from '../../auth/auth.model';
import { AuthService } from '../../auth/auth.service';
import { openChat } from '../../coaching-commitment/commitment/override-commitment.function';
import { TabRefId } from '../../coaching-detail';
import { matchUserId } from '../../coaching-detail/navigate';
import { scrollTranscriptIntoView } from '../../coaching/detail/pillar-two/transcript-summary/function/scroll-transcript-into-view.function';
import { TranscriptSummaryState } from '../../coaching/detail/pillar-two/transcript-summary/transcript-summary.state';
import { CoachingNewTranscriptDialogService } from '../../coaching/new-transcript-dialog';
import { useSelector2 } from '../../coaching/store-use-selector';
import { TranscriptDialogService } from '../../coaching/transcript-dialog';
import {
    takeUntilDestroy,
    TakeUntilDestroy,
} from '../../common/take-until-destroy.decorator';
import {
    EventCentreDialogService,
    EventCentreHook,
    EventCentreHookService,
} from '../../notification/event-centre';
import { Item, ItemAction } from '../../notification/event-centre/core';
import { Platform } from '../../platform-call';
import { CoachingSwitchboardService } from '../switchboard';

import { Command } from './command';

@Injectable({ providedIn: 'root' })
@TakeUntilDestroy()
export class CoachingEventCentreHookService
    implements OnDestroy, EventCentreHook
{
    private _dialogRefs: Array<CmpsDialogRef<unknown, unknown>>;

    constructor(
        private readonly _router: Router,
        private readonly _hookService: EventCentreHookService,
        private readonly _dialogService: EventCentreDialogService,
        private readonly _ntcDialogService: CoachingNewTranscriptDialogService,
        private readonly _authService: AuthService,
        private readonly _transcriptDialogService: TranscriptDialogService,
        private readonly _switchboardService: CoachingSwitchboardService,
    ) {
        this._dialogRefs = [];

        this._bindAuth();

        this._bindTranscriptSummary();

        this._hookService.register(this);
    }

    ngOnDestroy(): void {
        this._hookService.unregister(this);

        this._dialogRefs = [];
    }

    ecOnActionInvoked(item: Item, action: ItemAction): boolean {
        if (!action.command?.toLowerCase()?.startsWith('?command=coaching:')) {
            return false;
        }

        this._checkCommand(item, action.command);
    }

    ecOnItemsChanged(items: Item[]): void {
        items.forEach((item) => {
            const extensions = item.extensions?.filter(
                (e) => e.toLowerCase()?.startsWith('?command=coaching:'),
            );
            extensions?.forEach((command) => {
                if (!item.read) {
                    this._checkCommand(item, command);
                }
            });
        });
    }

    private _bindAuth(): void {
        this._authService.authStatus
            .pipe(
                tap((authStatus) => {
                    if (
                        [
                            AuthState.LOGOUT,
                            AuthState.TIMEOUT,
                            AuthState.LOST,
                        ].includes(this._authService.authState) &&
                        authStatus !== AuthStatus.AUTHENTICATED
                    ) {
                        const dialogRefs = [...this._dialogRefs];
                        dialogRefs.forEach((e) => e.close());
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _bindTranscriptSummary(): void {
        let start: string;
        let end: string;
        this._router.events
            .pipe(
                tap((event) => {
                    if (event instanceof NavigationStart) {
                        start = this._router.url;
                    }
                    if (event instanceof NavigationEnd) {
                        end = this._router.url;
                    }
                }),
                filter((event) => {
                    return event instanceof NavigationEnd;
                }),
                switchMap(() => {
                    const startUserId = matchUserId(start)?.[1];
                    const endUserId = matchUserId(end)?.[1];
                    const wasNotOnCoachingDetail = startUserId == null;
                    const differentUser =
                        startUserId != null &&
                        endUserId != null &&
                        startUserId !== endUserId;
                    if (wasNotOnCoachingDetail || differentUser) {
                        return waitForReload();
                    }
                    this._checkCommandFromUrl();
                    return of(null);
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        const waitForReload = (): Observable<any> => {
            let cancel = false;
            return useSelector2<any>(
                (state) =>
                    (
                        state?.coachingDetail
                            ?.transcriptSummary as TranscriptSummaryState
                    )?.pending,
            ).pipe(
                takeWhile(() => cancel === false),
                tap(([prevState, state]) => {
                    if (prevState?.reload === true && state?.reload !== true) {
                        cancel = true;
                        this._checkCommandFromUrl();
                    }
                }),
            );
        };
    }

    private _checkCommandFromUrl(): void {
        try {
            const commandB64 = this._router
                .parseUrl(this._router.url)
                .queryParamMap.get('coachingCommand');
            const command = atob(commandB64);
            this._checkCommand(null, command);
        } catch {}
    }

    private _checkCommand(item: Item, command: string): boolean {
        const params = new URLSearchParams(command);
        const commandStr = params.get('command');

        switch (commandStr.toLowerCase()) {
            case Command.OPEN_CHAT.toLowerCase():
                openChat(params);
                return true;
            case Command.OPEN_NEW_TRANSCRIPT_DIALOG.toLowerCase(): {
                this._openTranscriptDialog(item, params);
                return false;
            }
            case Command.OPEN_TRANSCRIPT.toLowerCase():
                this._openTranscript(params);
                return true;
            default:
                return false;
        }
    }

    private _openTranscriptDialog(item: Item, params: URLSearchParams): void {
        let ref: CmpsDialogRef<unknown, unknown> | null;
        timer(2000)
            .pipe(
                filter(
                    () =>
                        this._authService.authStatus.value ===
                        AuthStatus.AUTHENTICATED,
                ),
                switchMap(() => {
                    const ret = this._ntcDialogService.createDialog(params);
                    ref = ret.ref;
                    this._dialogRefs.push(ret.ref);
                    return ret.obs;
                }),
                tap(() => {
                    const index = this._dialogRefs.indexOf(ref);
                    if (index !== -1) {
                        this._dialogRefs.splice(index, 1);
                    }
                }),
                filter(
                    () =>
                        this._authService.authStatus.value ===
                        AuthStatus.AUTHENTICATED,
                ),
                tap(() => {
                    this._dialogService.updateItem(item, true, item.deleted);
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _openTranscript(params: URLSearchParams): void {
        /* TODO remove platform fallback in the future */
        const platform = (params.get('platform') ??
            Platform.MICROSOFT) as Platform;
        const transcriptId = parseInt(params.get('transcriptId'));

        this._transcriptDialogService.open(platform, transcriptId);

        void this._router.navigate([], {
            queryParams: { coachingCommand: undefined },
            queryParamsHandling: 'merge',
            replaceUrl: true,
        });

        this._switchboardService.transcriptSummary.sbReload([transcriptId]);

        /* TODO revise. TabRefId might be a heavy circular import */
        this._switchboardService.pillarTwo.sbSwitchTab(TabRefId.TRANSCRIPTS);

        scrollTranscriptIntoView(transcriptId);
    }
}
